import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Toast from '../components/Toast';
import { API, urlFichier } from '../api';
import { C, E, RAYON, T, champ as champStyle } from '../theme';
import { IconeCorbeille, IconeCrayon } from '../components/Icones';

// Repères communs à cette page (voir src/theme.js).
const carteSection = {
  background: C.surface, borderRadius: RAYON.lg, padding: E.lg,
  border: `1px solid ${C.bordure}`,
};
const titreSection = { fontSize: '14px', fontWeight: 600, color: C.texte,
                       margin: `0 0 ${E.lg}` };


// Exporté : la création d'une affaire chaude assurance (Prospects.js) propose
// les mêmes types. Une copie de plus dans le CRM et un type ajouté ici
// s'afficherait en code brut là-bas. Le site vitrine en garde une copie à lui,
// hors de portée d'un import — voir CLAUDE.md.
export const TYPE_CONTRAT_LABELS = {
  flotte:       'Flotte auto',
  rc:           'RC Professionnelle',
  rc_transport: 'RC Transport',
  batiment:     'Bâtiment',
  prevoyance:   'Prévoyance',
  mutuelle:     'Mutuelle',
  vehicule:     'Véhicule',
};

// Compagnies proposées dans la liste ; toute autre se saisit après « Autre… ».
const COMPAGNIES_PROPOSEES = ['Helvetia', 'Abeille'];

const FRACTIONNEMENT_LABELS = {
  1:  'Annuel',
  2:  'Semestriel',
  4:  'Trimestriel',
  6:  'Bimestriel',
  12: 'Mensuel',
};

const CATEGORIE_LABELS = {
  degat:         'Dégât matériel',
  pas_de_degat:  'Pas de dégât',
  mise_en_cause: 'Mise en cause',
  recours_subi:  'Recours subi',
  sans_expertise:'Sans expertise',
};

const STATUT_CONTRAT_COLORS = {
  'Actif':      { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  'Résilié':    { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
  'En attente': { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
};

const STATUT_SIN_COLORS = {
  'Ouvert':   { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  'En cours': { bg: '#dbeafe', text: '#1d4ed8', border: '#bfdbfe' },
  'Clos':     { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' },
};

const RESPONSABILITE_OPTIONS = [
  'Client responsable',
  'Tiers responsable',
  'Responsabilité partagée',
  'Non déterminée',
];

const RESPONSABILITE_COLORS = {
  'Client responsable':      { bg: '#fff7ed', text: '#c2410c', border: '#fed7aa' },
  'Tiers responsable':       { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  'Responsabilité partagée': { bg: '#fdf4ff', text: '#7e22ce', border: '#e9d5ff' },
  'Non déterminée':          { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' },
};

const CATEGORIE_COLORS = {
  degat:         { bg: '#fee2e2', text: '#991b1b' },
  pas_de_degat:  { bg: '#d1fae5', text: '#065f46' },
  mise_en_cause: { bg: '#fff7ed', text: '#c2410c' },
  recours_subi:  { bg: '#fdf4ff', text: '#7e22ce' },
  sans_expertise:{ bg: '#f0f9ff', text: '#0369a1' },
};

const todayStr = () => new Date().toISOString().split('T')[0];

function formatPlaque(raw) {
  const clean = raw.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  let part1 = '', part2 = '', part3 = '';
  for (const ch of clean) {
    if (part1.length < 2) {
      if (/[A-Z]/.test(ch)) part1 += ch;
    } else if (part2.length < 3) {
      if (/[0-9]/.test(ch)) part2 += ch;
    } else if (part3.length < 2) {
      if (/[A-Z]/.test(ch)) part3 += ch;
    }
  }
  let result = part1;
  if (part2) result += '-' + part2;
  if (part3) result += '-' + part3;
  return result;
}

const inputStyle = champStyle;
const labelStyle = { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#6b7280' };

const RECOURS_CATS = ['mise_en_cause', 'recours_subi'];

function getNextStep(s) {
  if (!s || s.statut === 'Clos') return null;
  if (RECOURS_CATS.includes(s.categorie)) {
    if (!s.date_rapport_expertise) return 'facture_transmise';
    if (!s.date_reglement)         return 'reglement_facture';
    if (s.statut !== 'Clos')       return 'cloture';
    return null;
  }
  if (!s.num_sinistre_assurance) return 'num_sinistre';
  if (s.categorie === 'sans_expertise') {
    if (!s.date_rapport_expertise) return 'facture';
    return 'cloture';
  }
  if (!s.date_rapport_expertise && s.categorie !== 'pas_de_degat') return 'rapport';
  if (s.statut !== 'Clos') return 'cloture';
  return null;
}

function getStepLabel(s) {
  if (s.statut === 'Clos') return '✅ Clos';
  if (RECOURS_CATS.includes(s.categorie)) {
    if (!s.date_rapport_expertise) return '📄 Étape 1 — Facture à transmettre';
    if (!s.date_reglement)         return '💶 Étape 2 — Règlement facture en attente';
    return '📋 Étape 3 — Règlement reçu, prêt à clore';
  }
  if (s.categorie === 'sans_expertise') {
    if (!s.num_sinistre_assurance) return '⏳ Étape 2 — En attente n° assurance';
    if (!s.date_rapport_expertise) return '🧾 Étape 3 — En attente facture client';
    return '📋 Étape 4 — Facture reçue, prêt à clore';
  }
  if (s.date_rapport_expertise) return '📋 Étape 4 — Rapport reçu';
  if (s.num_sinistre_assurance)  return '📤 Étape 3 — Mission envoyée';
  return '⏳ Étape 2 — En attente n° assurance';
}

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; }
function fmtEur(v) { return v != null ? Number(v).toLocaleString('fr-FR') + ' €' : '—'; }

// ────────────────────────────────────────────────────────────
export default function Assurance({ prospectId, user }) {
  const [activeTab, setActiveTab] = useState('contrats');
  const [contrats, setContrats] = useState([]);
  const [sinistres, setSinistres] = useState([]);
  const [toast, setToast] = useState(null);

  // Contrat
  const [showContratForm, setShowContratForm] = useState(false);
  const [editContratId, setEditContratId] = useState(null);
  const [modeProposition, setModeProposition] = useState(false);
  // « Autre » se retient à part du nom de la compagnie. Auparavant, choisir
  // « Autre » vidait la compagnie, et le champ de saisie ne s'affichait que si
  // elle n'était PAS vide : la condition s'annulait elle-même, le champ n'a
  // jamais pu apparaître depuis sa création.
  const [compagnieAutre, setCompagnieAutre] = useState(false);
  const [contratForm, setContratForm] = useState({ type_contrat: 'flotte', num_contrat: '', date_debut: '', date_echeance: '', prime_annuelle: '', fractionnement_paiement: 1, statut: 'En attente', compagnie: '' });

  // Modal sinistre — null | { mode:'create'|'advance', step:1|2, form:{}, createdId, sinistre }
  const [sinistreModal, setSinistreModal] = useState(null);
  const [advanceData, setAdvanceData] = useState({});

  // Modal documents contrat
  const [contratDocsModal, setContratDocsModal] = useState(null); // contrat object | null

  // Modal appels de prime
  const [appelPrimeModal, setAppelPrimeModal] = useState(null); // contrat object | null

  useEffect(() => { fetchAll(); }, [prospectId]);

  const fetchAll = () => {
    axios.get(`${API}/prospects/${prospectId}/contrats-assurance`).then(r => setContrats(r.data)).catch(() => {});
    axios.get(`${API}/prospects/${prospectId}/sinistres`).then(r => setSinistres(r.data)).catch(() => {});
  };

  // ── Contrats ──────────────────────────────────────────────
  const submitContrat = () => {
    // « Autre » sans nom enregistrerait un contrat sans compagnie, alors que
    // l'utilisateur a justement voulu en préciser une.
    if (compagnieAutre && !(contratForm.compagnie || '').trim()) {
      setToast({ message: 'Saisissez le nom de la compagnie.', type: 'error' });
      return;
    }
    const payload = {
      ...contratForm,
      prospect_id: prospectId,
      prime_annuelle: parseFloat(contratForm.prime_annuelle) || null,
      fractionnement_paiement: parseInt(contratForm.fractionnement_paiement) || 1,
      date_debut: contratForm.date_debut || null,
      date_echeance: contratForm.date_echeance || null,
    };
    const req = editContratId
      ? axios.put(`${API}/contrats-assurance/${editContratId}`, payload)
      : axios.post(`${API}/contrats-assurance`, payload);
    req.then(() => {
      fetchAll();
      setShowContratForm(false);
      setEditContratId(null);
      setContratForm({ type_contrat: 'flotte', num_contrat: '', date_debut: '', date_echeance: '', prime_annuelle: '', fractionnement_paiement: 1, statut: 'En attente', compagnie: '' });
      setToast({ message: editContratId ? 'Contrat mis à jour !' : 'Contrat créé !', type: 'success' });
    });
  };

  // Une « proposition » est un contrat pressenti : ni acquis, ni signé. Ce
  // n'est pas un état de plus en base — c'est le statut « En attente », celui
  // que le tableau de bord compte déjà comme affaire chaude d'assurance. Seul
  // le formulaire change : pas de numéro de contrat à saisir, puisqu'il n'y a
  // pas encore de contrat.
  useEffect(() => {
    if (!showContratForm) return;
    const nom = contratForm.compagnie || '';
    setCompagnieAutre(nom !== '' && !COMPAGNIES_PROPOSEES.includes(nom));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showContratForm, editContratId]);

  const openEditContrat = (c) => {
    setModeProposition(false);
    setEditContratId(c.id);
    setContratForm({ type_contrat: c.type_contrat || 'flotte', num_contrat: c.num_contrat || '', date_debut: c.date_debut || '', date_echeance: c.date_echeance || '', prime_annuelle: c.prime_annuelle != null ? String(c.prime_annuelle) : '', fractionnement_paiement: c.fractionnement_paiement || 1, statut: c.statut || 'En attente', compagnie: c.compagnie || '' });
    setShowContratForm(true);
  };

  const deleteContrat = (c) => {
    const affaire = c.statut === 'En attente';
    // La confirmation nomme ce qu'on supprime : « Supprimer ce contrat ? » ne
    // dit pas lequel, et deux lignes se ressemblent vite.
    const nom = `${TYPE_CONTRAT_LABELS[c.type_contrat] || c.type_contrat || 'contrat'}${c.compagnie ? ` · ${c.compagnie}` : ''} (${c.numero})`;
    if (!window.confirm(affaire
      ? `Supprimer cette affaire chaude d'assurance ?\n\n${nom}\n\nElle ne comptera plus dans les affaires chaudes.`
      : `Supprimer ce contrat ?\n\n${nom}`)) return;
    axios.delete(`${API}/contrats-assurance/${c.id}`)
      .then(() => { fetchAll(); setToast({ message: affaire ? 'Affaire supprimée.' : 'Contrat supprimé.', type: 'success' }); })
      .catch(err => setToast({ message: err.response?.data?.detail || 'La suppression a échoué.', type: 'error' }));
  };

  // ── Sinistres — création ───────────────────────────────────
  const submitNewSinistreStep1 = async (form) => {
    const payload = {
      prospect_id: prospectId,
      num_contrat_assurance: form.num_contrat_assurance || null,
      plaque_immatriculation: form.plaque || null,
      type_vehicule: form.type_vehicule || null,
      date_sinistre: form.date_sinistre || null,
      categorie: form.categorie,
      statut: 'Ouvert',
    };
    try {
      const res = await axios.post(`${API}/sinistres`, payload);
      fetchAll();
      if (form.categorie === 'pas_de_degat') {
        setSinistreModal({ mode: 'create', step: 2, createdId: res.data.id, form: {} });
      } else {
        setSinistreModal(null);
        setToast({ message: `Sinistre ${res.data.numero} créé. Avancez le workflow dès réception du n° assurance.`, type: 'success' });
      }
    } catch { setToast({ message: 'Erreur lors de la création.', type: 'error' }); }
  };

  const submitNewSinistreStep2PasDegat = async () => {
    const { createdId, form } = sinistreModal;
    try {
      await axios.put(`${API}/sinistres/${createdId}`, { num_sinistre_assurance: form.num_sinistre_assurance, statut: 'Clos', date_cloture: todayStr() });
      fetchAll();
      setSinistreModal(null);
      setToast({ message: 'Sinistre clos (pas de dégât).', type: 'success' });
    } catch { setToast({ message: 'Erreur lors de la clôture.', type: 'error' }); }
  };

  // ── Sinistres — édition informations ─────────────────────
  const openEditSinistre = (s) => {
    setSinistreModal({
      mode: 'edit', sinistre: s,
      form: {
        plaque_immatriculation: s.plaque_immatriculation || '',
        type_vehicule:          s.type_vehicule          || '',
        categorie:              s.categorie              || 'degat',
        num_contrat_assurance:  s.num_contrat_assurance  || '',
        date_sinistre:          s.date_sinistre          || '',
        num_sinistre_assurance: s.num_sinistre_assurance || '',
        statut:                 s.statut                 || 'Ouvert',
        date_ordre_mission:     s.date_ordre_mission     || '',
        date_rapport_expertise: s.date_rapport_expertise || '',
        date_cloture:           s.date_cloture           || '',
        commentaire_cloture:    s.commentaire_cloture    || '',
        montant_final:          s.montant_final != null ? String(s.montant_final) : '',
        responsabilite:         s.responsabilite         || '',
      },
    });
  };

  const submitEditSinistre = async () => {
    const { sinistre, form } = sinistreModal;
    const payload = {
      plaque_immatriculation: form.plaque_immatriculation || null,
      type_vehicule:          form.type_vehicule          || null,
      categorie:              form.categorie              || null,
      num_contrat_assurance:  form.num_contrat_assurance  || null,
      date_sinistre:          form.date_sinistre          || null,
      num_sinistre_assurance: form.num_sinistre_assurance || null,
      statut:                 form.statut                 || null,
      date_ordre_mission:     form.date_ordre_mission     || null,
      date_rapport_expertise: form.date_rapport_expertise || null,
      date_cloture:           form.date_cloture           || null,
      commentaire_cloture:    form.commentaire_cloture    || null,
      montant_final:          parseFloat(form.montant_final)  || null,
      responsabilite:         form.responsabilite         || null,
    };
    try {
      await axios.put(`${API}/sinistres/${sinistre.id}`, payload);
      fetchAll();
      setSinistreModal(null);
      setToast({ message: 'Sinistre modifié !', type: 'success' });
    } catch { setToast({ message: 'Erreur lors de la modification.', type: 'error' }); }
  };

  // ── Sinistres — avancement workflow ───────────────────────
  const openAdvance = (s) => {
    const nextStep = getNextStep(s);
    setAdvanceData(nextStep === 'num_sinistre' ? { date_ordre_mission: todayStr() } : {});
    setSinistreModal({ mode: 'advance', sinistre: s });
  };

  const submitAdvance = async () => {
    const { sinistre } = sinistreModal;
    const nextStep = getNextStep(sinistre);
    let payload = {};
    if (nextStep === 'num_sinistre') {
      payload = { num_sinistre_assurance: advanceData.num_sinistre_assurance, date_ordre_mission: advanceData.date_ordre_mission || todayStr(), statut: 'En cours' };
    } else if (nextStep === 'rapport') {
      payload = { date_rapport_expertise: advanceData.date_rapport_expertise };
    } else if (nextStep === 'facture') {
      payload = { date_rapport_expertise: todayStr() };
    } else if (nextStep === 'facture_transmise') {
      payload = { date_rapport_expertise: advanceData.date_facture_transmise || todayStr(), statut: 'En cours' };
    } else if (nextStep === 'reglement_facture') {
      payload = {
        date_reglement: advanceData.date_reglement || todayStr(),
        montant_reglement: parseFloat(advanceData.montant_reglement) || null,
      };
    } else if (nextStep === 'cloture') {
      payload = {
        date_cloture:        advanceData.date_cloture        || todayStr(),
        commentaire_cloture: advanceData.commentaire_cloture || null,
        montant_final:       parseFloat(advanceData.montant_final) || null,
        responsabilite:      advanceData.responsabilite      || null,
        statut:              'Clos',
      };
    }
    try {
      await axios.put(`${API}/sinistres/${sinistre.id}`, payload);
      fetchAll();
      setSinistreModal(null);
      setAdvanceData({});
      setToast({ message: 'Sinistre avancé !', type: 'success' });
    } catch { setToast({ message: 'Erreur lors de la mise à jour.', type: 'error' }); }
  };

  const deleteSinistre = (id) => {
    if (!window.confirm('Supprimer ce sinistre ?')) return;
    axios.delete(`${API}/sinistres/${id}`).then(() => { fetchAll(); setToast({ message: 'Sinistre supprimé.', type: 'success' }); });
  };

  // Une proposition est un contrat au statut « En attente » : même table, même
  // formulaire. Ce qui change, c'est l'endroit où on la lit — mélangée aux
  // contrats signés, elle passait inaperçue, et on se demandait où elle était
  // partie.
  const propositions = contrats.filter(c => c.statut === 'En attente');
  const contratsSignes = contrats.filter(c => c.statut !== 'En attente');
  const surPropositions = activeTab === 'propositions';
  const listeAffichee = surPropositions ? propositions : contratsSignes;

  // ── Rendu ──────────────────────────────────────────────────
  return (
    <div style={{ ...carteSection, marginBottom: E.lg }}>
      <h2 style={titreSection}>Assurance</h2>

      {/* Onglets */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '1px solid #e5e7eb' }}>
        {[
          { key: 'contrats',     label: `📋 Contrats (${contratsSignes.length})` },
          { key: 'propositions', label: `📝 Propositions (${propositions.length})` },
          { key: 'sinistres',    label: `🚗 Sinistres (${sinistres.length})` },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: '9px 20px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '14px', fontWeight: activeTab === tab.key ? '600' : '400',
            color: activeTab === tab.key ? '#1a2b4a' : '#6b7280',
            borderBottom: `2px solid ${activeTab === tab.key ? '#1a2b4a' : 'transparent'}`,
            marginBottom: '-1px',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tabs Contrats et Propositions ─────────────────── */}
      {(activeTab === 'contrats' || activeTab === 'propositions') && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '16px' }}>
            {!surPropositions && (
            <button onClick={() => { setModeProposition(true); setEditContratId(null); setContratForm({ type_contrat: 'flotte', num_contrat: '', date_debut: '', date_echeance: '', prime_annuelle: '', fractionnement_paiement: 1, statut: 'En attente', compagnie: '' }); setShowContratForm(true); }}
              style={{ background: 'white', color: '#1a2b4a', padding: '8px 16px', border: '1px solid #1a2b4a', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
              + Proposition
            </button>
            )}
            {surPropositions ? (
            <button onClick={() => { setModeProposition(true); setEditContratId(null); setContratForm({ type_contrat: 'flotte', num_contrat: '', date_debut: '', date_echeance: '', prime_annuelle: '', fractionnement_paiement: 1, statut: 'En attente', compagnie: '' }); setShowContratForm(true); }}
              style={{ background: '#1a2b4a', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
              + Proposition
            </button>
            ) : (
            <button onClick={() => { setModeProposition(false); setEditContratId(null); setContratForm({ type_contrat: 'flotte', num_contrat: '', date_debut: '', date_echeance: '', prime_annuelle: '', fractionnement_paiement: 1, statut: 'En attente', compagnie: '' }); setShowContratForm(true); }}
              style={{ background: '#1a2b4a', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
              + Nouveau contrat
            </button>
            )}
          </div>

          {showContratForm && (
            <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
              <h3 style={titreSection}>
                {editContratId ? 'Modifier'
                  : modeProposition ? 'Nouvelle proposition' : 'Nouveau contrat'}
              </h3>
              {modeProposition && !editContratId && (
                <p style={{ fontSize: '12.5px', color: '#6b7280', margin: '-8px 0 14px' }}>
                  Contrat pressenti, ni acquis ni signé. Il compte dès maintenant
                  parmi les affaires chaudes d'assurance ; le numéro se renseignera
                  à la signature.
                </p>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label style={labelStyle}>Type de contrat</label>
                  <select value={contratForm.type_contrat} onChange={e => setContratForm({ ...contratForm, type_contrat: e.target.value })} style={inputStyle}>
                    {Object.entries(TYPE_CONTRAT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                {/* Pas de numéro sur une proposition : il n'existe pas encore.
                    Un champ vide proposé à la saisie invite à en inventer un. */}
                {!modeProposition && (
                  <div>
                    <label style={labelStyle}>N° contrat</label>
                    <input value={contratForm.num_contrat} onChange={e => setContratForm({ ...contratForm, num_contrat: e.target.value })} style={inputStyle} />
                  </div>
                )}
                <div>
                  <label style={labelStyle}>Date début</label>
                  <input type="date" value={contratForm.date_debut} onChange={e => setContratForm({ ...contratForm, date_debut: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Date échéance</label>
                  <input type="date" value={contratForm.date_echeance} onChange={e => setContratForm({ ...contratForm, date_echeance: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Prime annuelle (€)</label>
                  <input type="number" value={contratForm.prime_annuelle} onChange={e => setContratForm({ ...contratForm, prime_annuelle: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Fractionnement</label>
                  <select value={contratForm.fractionnement_paiement} onChange={e => setContratForm({ ...contratForm, fractionnement_paiement: parseInt(e.target.value) })} style={inputStyle}>
                    {Object.entries(FRACTIONNEMENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l} ({v === '1' ? '1x/an' : `${v}x/an`})</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Statut</label>
                  <select value={contratForm.statut} onChange={e => setContratForm({ ...contratForm, statut: e.target.value })} style={inputStyle}>
                    {['En attente', 'Actif', 'Résilié'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Compagnie</label>
                  <select
                    value={compagnieAutre ? 'Autre' : (COMPAGNIES_PROPOSEES.includes(contratForm.compagnie) ? contratForm.compagnie : '')}
                    onChange={e => {
                      const choix = e.target.value;
                      setCompagnieAutre(choix === 'Autre');
                      // Passer à « Autre » vide le nom, pour saisir le bon ;
                      // revenir à une compagnie de la liste le remplace.
                      setContratForm({ ...contratForm, compagnie: choix === 'Autre' ? '' : choix });
                    }}
                    style={inputStyle}>
                    <option value="">— Sélectionner —</option>
                    {COMPAGNIES_PROPOSEES.map(nom => <option key={nom} value={nom}>{nom}</option>)}
                    <option value="Autre">Autre…</option>
                  </select>
                </div>
                {compagnieAutre && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Nom de la compagnie</label>
                    <input value={contratForm.compagnie} onChange={e => setContratForm({ ...contratForm, compagnie: e.target.value })} placeholder="Nom de la compagnie" style={inputStyle} autoFocus />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={submitContrat} style={{ background: '#1a2b4a', color: 'white', padding: '9px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>Enregistrer</button>
                <button onClick={() => setShowContratForm(false)} style={{ background: 'white', color: '#6b7280', padding: '9px 16px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Annuler</button>
              </div>
            </div>
          )}

          {listeAffichee.length === 0 && !showContratForm ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: '14px' }}>
              {surPropositions
                ? "Aucune proposition en cours. Une proposition est un contrat pressenti, pas encore signé — elle compte déjà parmi les affaires chaudes d'assurance."
                : "Aucun contrat d'assurance. Les contrats souscrits pour vos clients apparaîtront ici."}
            </div>
          ) : listeAffichee.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    {/* Pas de colonne « N° contrat » sur les propositions : il
                        n'existe pas encore, la colonne serait vide de bout en bout. */}
                    {['N°', 'Type', 'Compagnie', ...(surPropositions ? [] : ['N° contrat']),
                      'Début', 'Échéance', 'Prime/an', 'Fractionnement', 'Statut', ''].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {listeAffichee.map(c => {
                    const sc = STATUT_CONTRAT_COLORS[c.statut] || STATUT_CONTRAT_COLORS['En attente'];
                    return (
                      <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '11px 12px', fontSize: '12px', fontWeight: '700', color: '#1a2b4a', fontFamily: 'monospace' }}>{c.numero}</td>
                        <td style={{ padding: '11px 12px', fontSize: '13px', color: '#374151' }}>{TYPE_CONTRAT_LABELS[c.type_contrat] || c.type_contrat || '—'}</td>
                        <td style={{ padding: '11px 12px', fontSize: '13px', color: '#374151' }}>{c.compagnie || '—'}</td>
                        {!surPropositions && (
                          <td style={{ padding: '11px 12px', fontSize: '13px', color: '#374151' }}>{c.num_contrat || '—'}</td>
                        )}
                        <td style={{ padding: '11px 12px', fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(c.date_debut)}</td>
                        <td style={{ padding: '11px 12px', fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(c.date_echeance)}</td>
                        <td style={{ padding: '11px 12px', fontSize: '13px', fontWeight: '600', color: '#059669' }}>{fmtEur(c.prime_annuelle)}</td>
                        <td style={{ padding: '11px 12px', fontSize: '12px', color: '#374151', whiteSpace: 'nowrap' }}>
                          {c.fractionnement_paiement && c.fractionnement_paiement !== 1
                            ? <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: '600', border: '1px solid #bfdbfe' }}>{FRACTIONNEMENT_LABELS[c.fractionnement_paiement] || c.fractionnement_paiement + 'x/an'}</span>
                            : <span style={{ color: '#9ca3af', fontSize: '12px' }}>Annuel</span>
                          }
                        </td>
                        <td style={{ padding: '11px 12px' }}>
                          <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: '2px 9px', borderRadius: '99px', fontSize: '11px', fontWeight: '600' }}>{c.statut}</span>
                        </td>
                        <td style={{ padding: '11px 12px' }}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap' }}>
                            <button onClick={() => {
                              setActiveTab('sinistres');
                              setSinistreModal({ mode: 'create', step: 1, form: { date_sinistre: todayStr(), num_contrat_assurance: c.num_contrat || '', plaque: '', type_vehicule: '', categorie: 'degat' } });
                            }} style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '500', whiteSpace: 'nowrap' }}>Sinistre
                            </button>
                            <button title="Modifier" onClick={() => openEditContrat(c)} style={{ background: 'none', border: '1px solid #e5e7eb', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}><IconeCrayon taille={15} /></button>
                            <button onClick={() => setAppelPrimeModal(c)} style={{ background: '#fefce8', border: '1px solid #fde68a', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '500', color: '#92400e', whiteSpace: 'nowrap' }}>Appels</button>
                            <button onClick={() => setContratDocsModal(c)} style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#1d4ed8' }}>📎</button>
                            {/* Une affaire chaude (statut « En attente ») ne se supprime
                                que par un admin ; le serveur le refuse aux autres. */}
                            {(c.statut !== 'En attente' || user?.role === 'admin') && (
                              <button title="Supprimer" onClick={() => deleteContrat(c)} style={{ background: 'none', border: '1px solid #fecaca', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#ef4444' }}><IconeCorbeille taille={15} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tab Sinistres ──────────────────────────────────── */}
      {activeTab === 'sinistres' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button onClick={() => setSinistreModal({ mode: 'create', step: 1, form: { date_sinistre: todayStr(), num_contrat_assurance: '', plaque: '', type_vehicule: '', categorie: 'degat' } })}
              style={{ background: '#1a2b4a', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
              + Nouveau sinistre
            </button>
          </div>

          {sinistres.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: '14px' }}>Aucun sinistre déclaré. Les déclarations et leur suivi apparaîtront ici.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1050px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    {['N°', 'N° contrat', 'Plaque', 'Type véhicule', 'Date sinistre', 'Catégorie', 'Statut', 'Responsabilité', 'N° assurance', 'Étape', ''].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sinistres.map(s => {
                    const sc = STATUT_SIN_COLORS[s.statut] || STATUT_SIN_COLORS['Ouvert'];
                    const cc = CATEGORIE_COLORS[s.categorie] || { bg: '#f3f4f6', text: '#374151' };
                    const canAdvance = getNextStep(s) !== null;
                    return (
                      <tr key={s.id} onClick={() => setSinistreModal({ mode: 'detail', sinistre: s })} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                        <td style={{ padding: '11px 12px', fontSize: '12px', fontWeight: '700', color: '#1a2b4a', fontFamily: 'monospace' }}>{s.numero}</td>
                        <td style={{ padding: '11px 12px', fontSize: '13px', color: '#374151' }}>{s.num_contrat_assurance || '—'}</td>
                        <td style={{ padding: '11px 12px', fontSize: '13px', fontWeight: '600', color: '#1a2b4a' }}>{s.plaque_immatriculation || '—'}</td>
                        <td style={{ padding: '11px 12px', fontSize: '13px', color: '#6b7280' }}>{s.type_vehicule || '—'}</td>
                        <td style={{ padding: '11px 12px', fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(s.date_sinistre)}</td>
                        <td style={{ padding: '11px 12px' }}>
                          <span style={{ background: cc.bg, color: cc.text, padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                            {CATEGORIE_LABELS[s.categorie] || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '11px 12px' }}>
                          <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: '2px 9px', borderRadius: '99px', fontSize: '11px', fontWeight: '600' }}>{s.statut}</span>
                        </td>
                        <td style={{ padding: '11px 12px' }}>
                          {s.responsabilite ? (
                            <span style={{ background: (RESPONSABILITE_COLORS[s.responsabilite] || {}).bg || '#f3f4f6', color: (RESPONSABILITE_COLORS[s.responsabilite] || {}).text || '#6b7280', border: `1px solid ${(RESPONSABILITE_COLORS[s.responsabilite] || {}).border || '#d1d5db'}`, padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                              {s.responsabilite}
                            </span>
                          ) : <span style={{ color: '#d1d5db', fontSize: '12px' }}>—</span>}
                        </td>
                        <td style={{ padding: '11px 12px', fontSize: '12px', color: '#374151', fontFamily: 'monospace' }}>{s.num_sinistre_assurance || '—'}</td>
                        <td style={{ padding: '11px 12px', fontSize: '12px', color: '#6b7280' }}>{getStepLabel(s)}</td>
                        <td style={{ padding: '11px 12px' }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            {canAdvance && (
                              <button onClick={() => openAdvance(s)}
                                style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' }}>
                                Avancer →
                              </button>
                            )}
                            <button title="Modifier" onClick={() => openEditSinistre(s)} style={{ background: 'none', border: '1px solid #e5e7eb', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}><IconeCrayon taille={15} /></button>
                            <button title="Supprimer" onClick={() => deleteSinistre(s.id)} style={{ background: 'none', border: '1px solid #fecaca', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#ef4444' }}><IconeCorbeille taille={15} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────── */}
      {sinistreModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '500px', maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>

            {sinistreModal.mode === 'create' && sinistreModal.step === 1 && (
              <NewSinistreStep1
                form={sinistreModal.form}
                onChange={form => setSinistreModal({ ...sinistreModal, form })}
                onSubmit={() => submitNewSinistreStep1(sinistreModal.form)}
                onCancel={() => setSinistreModal(null)}
              />
            )}

            {sinistreModal.mode === 'create' && sinistreModal.step === 2 && (
              <PasDeDegatStep2
                form={sinistreModal.form}
                onChange={form => setSinistreModal({ ...sinistreModal, form })}
                onSubmit={submitNewSinistreStep2PasDegat}
                onCancel={() => setSinistreModal(null)}
              />
            )}

            {sinistreModal.mode === 'advance' && (
              <AdvanceSinistreModal
                sinistre={sinistreModal.sinistre}
                advanceData={advanceData}
                onChange={setAdvanceData}
                onSubmit={submitAdvance}
                onCancel={() => { setSinistreModal(null); setAdvanceData({}); }}
              />
            )}

            {sinistreModal.mode === 'edit' && (
              <EditSinistreModal
                sinistre={sinistreModal.sinistre}
                form={sinistreModal.form}
                onChange={form => setSinistreModal({ ...sinistreModal, form })}
                onSubmit={submitEditSinistre}
                onCancel={() => setSinistreModal(null)}
              />
            )}

            {sinistreModal.mode === 'detail' && (
              <SinistreDetailModal
                sinistre={sinistreModal.sinistre}
                onEdit={() => openEditSinistre(sinistreModal.sinistre)}
                onAdvance={() => openAdvance(sinistreModal.sinistre)}
                onClose={() => setSinistreModal(null)}
              />
            )}
          </div>
        </div>
      )}

      {contratDocsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '500px', maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <ContratDocumentsModal contrat={contratDocsModal} onClose={() => setContratDocsModal(null)} />
          </div>
        </div>
      )}

      {appelPrimeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '600px', maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <AppelPrimeModal contrat={appelPrimeModal} onClose={() => setAppelPrimeModal(null)} />
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ── Sous-composants ──────────────────────────────────────────

function ContratDocumentsModal({ contrat, onClose }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/contrats-assurance/${contrat.id}/documents`)
      .then(r => setDocs(r.data))
      .catch(() => {});
  }, [contrat.id]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/contrats-assurance/${contrat.id}/documents`, fd);
      setDocs(prev => [...prev, res.data]);
    } catch {
      alert('Erreur lors de l\'upload');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const supprimerDoc = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.nom_fichier}" ?`)) return;
    await axios.delete(`${API}/contrats-assurance/${contrat.id}/documents/${doc.id}`);
    setDocs(prev => prev.filter(d => d.id !== doc.id));
  };

  const getFileIcon = (nom) => {
    const ext = nom.split('.').pop().toLowerCase();
    if (ext === 'pdf') return '📄';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
    if (['doc', 'docx'].includes(ext)) return '📝';
    return '📎';
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h2 style={{ ...titreSection, marginBottom: E.xs }}>Pièces jointes</h2>
          <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>
            {contrat.numero}{contrat.num_contrat ? ` — ${contrat.num_contrat}` : ''}{' '}
            <span style={{ color: '#9ca3af' }}>{TYPE_CONTRAT_LABELS[contrat.type_contrat] || contrat.type_contrat}</span>
          </p>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '2px' }}>×</button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{ background: '#f0f7ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '7px 14px', borderRadius: '6px', cursor: uploading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', opacity: uploading ? 0.6 : 1 }}>
          {uploading ? 'Upload...' : '+ Ajouter un fichier'}
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx" style={{ display: 'none' }} onChange={handleFileChange} />
      </div>

      {docs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#d1d5db', fontSize: '14px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #e5e7eb' }}>
          Aucun document joint
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {docs.map(doc => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px' }}>
              <span style={{ fontSize: '16px', flexShrink: 0 }}>{getFileIcon(doc.nom_fichier)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#1a2b4a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.nom_fichier}</div>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>{new Date(doc.date_upload).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              <a href={urlFichier(`${API}/uploads/contrats/${doc.chemin}`)} target="_blank" rel="noopener noreferrer"
                style={{ background: '#1a2b4a', color: 'white', border: 'none', borderRadius: '5px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', textDecoration: 'none', flexShrink: 0 }}>
                ⬇ DL
              </a>
              <button title="Supprimer" onClick={() => supprimerDoc(doc)}
                style={{ background: 'none', border: '1px solid #fecaca', color: '#ef4444', borderRadius: '5px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}><IconeCorbeille taille={15} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', padding: '9px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
          Fermer
        </button>
      </div>
    </>
  );
}

function NewSinistreStep1({ form, onChange, onSubmit, onCancel }) {
  const isValid = !!(form.date_sinistre && form.categorie);
  return (
    <>
      <h2 style={{ ...titreSection, marginBottom: E.xs }}>Nouveau sinistre</h2>
      <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 20px' }}>Étape 1 — Déclaration initiale</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Date sinistre / envoi assurance *</label>
          <input type="date" value={form.date_sinistre} onChange={e => onChange({ ...form, date_sinistre: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>N° contrat assurance</label>
          <input value={form.num_contrat_assurance} onChange={e => onChange({ ...form, num_contrat_assurance: e.target.value })} placeholder="Ex: FL-2024-001" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Plaque d'immatriculation</label>
          <input value={form.plaque} onChange={e => onChange({ ...form, plaque: formatPlaque(e.target.value) })} placeholder="AB-123-CD" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Type de véhicule</label>
          <input value={form.type_vehicule} onChange={e => onChange({ ...form, type_vehicule: e.target.value })} placeholder="Tracteur, Semi, VL…" style={inputStyle} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Catégorie *</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {[
              { v: 'degat',         l: '🔧 Dégât matériel',  activeBg: '#fee2e2', activeBorder: '#fca5a5' },
              { v: 'pas_de_degat',  l: '✅ Pas de dégât',    activeBg: '#d1fae5', activeBorder: '#6ee7b7' },
              { v: 'mise_en_cause', l: '⚠️ Mise en cause',   activeBg: '#fff7ed', activeBorder: '#fed7aa' },
              { v: 'recours_subi',  l: '↩️ Recours subi',    activeBg: '#fdf4ff', activeBorder: '#e9d5ff' },
              { v: 'sans_expertise',l: '🧾 Sans expertise',  activeBg: '#f0f9ff', activeBorder: '#bae6fd' },
            ].map(({ v, l, activeBg, activeBorder }) => (
              <button key={v} onClick={() => onChange({ ...form, categorie: v })}
                style={{ padding: '9px 10px', borderRadius: '6px', border: `2px solid ${form.categorie === v ? activeBorder : '#e5e7eb'}`, background: form.categorie === v ? activeBg : 'white', cursor: 'pointer', fontSize: '12px', fontWeight: form.categorie === v ? '600' : '400', color: '#374151', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
      {form.categorie === 'pas_de_degat' && (
        <div style={{ background: '#f0fdf4', border: '1px solid #6ee7b7', borderRadius: '6px', padding: '10px 12px', fontSize: '12px', color: '#065f46', marginBottom: '16px' }}>Dossier sans dégât : vous saisirez le n° assurance à l'étape suivante et le dossier sera immédiatement clos.
        </div>
      )}
      {form.categorie === 'degat' && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '10px 12px', fontSize: '12px', color: '#1d4ed8', marginBottom: '16px' }}>
          ℹ️ Workflow : n° assurance → ordre de mission → rapport expertise → clôture. Avancez depuis le tableau des sinistres.
        </div>
      )}
      {['mise_en_cause', 'recours_subi'].includes(form.categorie) && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '10px 12px', fontSize: '12px', color: '#1d4ed8', marginBottom: '16px' }}>
          ℹ️ Workflow : facture transmise → règlement facture (date + montant) → clôture. Avancez depuis le tableau des sinistres.
        </div>
      )}
      {form.categorie === 'sans_expertise' && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', padding: '10px 12px', fontSize: '12px', color: '#0369a1', marginBottom: '16px' }}>Workflow : n° assurance → en attente facture client → clôture. Avancez depuis le tableau des sinistres.
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={onSubmit} disabled={!isValid}
          style={{ flex: 1, background: '#1a2b4a', color: 'white', padding: '11px', border: 'none', borderRadius: '8px', cursor: isValid ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '500', opacity: isValid ? 1 : 0.5 }}>
          {form.categorie === 'pas_de_degat' ? 'Suivant →' : 'Créer le sinistre'}
        </button>
        <button onClick={onCancel} style={{ padding: '11px 18px', background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Annuler</button>
      </div>
    </>
  );
}

function PasDeDegatStep2({ form, onChange, onSubmit, onCancel }) {
  const isValid = !!(form.num_sinistre_assurance);
  return (
    <>
      <h2 style={{ ...titreSection, marginBottom: E.xs }}>N° sinistre assurance</h2>
      <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 20px' }}>Dossier sans dégât — saisissez le n° fourni par l'assurance pour clore immédiatement.</p>
      <div style={{ marginBottom: '20px' }}>
        <label style={labelStyle}>N° sinistre assurance *</label>
        <input autoFocus value={form.num_sinistre_assurance || ''} onChange={e => onChange({ ...form, num_sinistre_assurance: e.target.value })} placeholder="Ex: HEL-2024-XXXXX" style={inputStyle} />
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={onSubmit} disabled={!isValid}
          style={{ flex: 1, background: '#059669', color: 'white', padding: '11px', border: 'none', borderRadius: '8px', cursor: isValid ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '500', opacity: isValid ? 1 : 0.5 }}>Clôturer le dossier
        </button>
        <button onClick={onCancel} style={{ padding: '11px 18px', background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Annuler</button>
      </div>
    </>
  );
}

function EditSinistreModal({ sinistre, form, onChange, onSubmit, onCancel }) {
  return (
    <>
      <h2 style={{ ...titreSection, marginBottom: E.xs }}>Modifier le sinistre</h2>
      <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 20px' }}>{sinistre.numero}</p>

      {/* Informations générales */}
      <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Informations générales</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        <div>
          <label style={labelStyle}>Date sinistre</label>
          <input type="date" value={form.date_sinistre} onChange={e => onChange({ ...form, date_sinistre: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>N° contrat assurance</label>
          <input value={form.num_contrat_assurance} onChange={e => onChange({ ...form, num_contrat_assurance: e.target.value })} placeholder="Ex: FL-2024-001" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Plaque d'immatriculation</label>
          <input value={form.plaque_immatriculation} onChange={e => onChange({ ...form, plaque_immatriculation: formatPlaque(e.target.value) })} placeholder="AB-123-CD" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Type de véhicule</label>
          <input value={form.type_vehicule} onChange={e => onChange({ ...form, type_vehicule: e.target.value })} placeholder="Tracteur, Semi, VL…" style={inputStyle} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>N° sinistre assurance</label>
          <input value={form.num_sinistre_assurance} onChange={e => onChange({ ...form, num_sinistre_assurance: e.target.value })} placeholder="Ex: HEL-2024-XXXXX" style={inputStyle} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Catégorie</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {[
              { v: 'degat',         l: '🔧 Dégât matériel',  activeBg: '#fee2e2', activeBorder: '#fca5a5' },
              { v: 'pas_de_degat',  l: '✅ Pas de dégât',    activeBg: '#d1fae5', activeBorder: '#6ee7b7' },
              { v: 'mise_en_cause', l: '⚠️ Mise en cause',   activeBg: '#fff7ed', activeBorder: '#fed7aa' },
              { v: 'recours_subi',  l: '↩️ Recours subi',    activeBg: '#fdf4ff', activeBorder: '#e9d5ff' },
              { v: 'sans_expertise',l: '🧾 Sans expertise',  activeBg: '#f0f9ff', activeBorder: '#bae6fd' },
            ].map(({ v, l, activeBg, activeBorder }) => (
              <button key={v} onClick={() => onChange({ ...form, categorie: v })}
                style={{ padding: '8px 10px', borderRadius: '6px', border: `2px solid ${form.categorie === v ? activeBorder : '#e5e7eb'}`, background: form.categorie === v ? activeBg : 'white', cursor: 'pointer', fontSize: '12px', fontWeight: form.categorie === v ? '600' : '400', color: '#374151', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Dates du workflow */}
      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginBottom: '20px' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Dates du workflow</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Statut</label>
            <select value={form.statut} onChange={e => onChange({ ...form, statut: e.target.value })} style={inputStyle}>
              {['Ouvert', 'En cours', 'Clos'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>{RECOURS_CATS.includes(sinistre.categorie) ? 'Date règlement' : 'Date ordre de mission'}</label>
            <input type="date" value={form.date_ordre_mission} onChange={e => onChange({ ...form, date_ordre_mission: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{RECOURS_CATS.includes(sinistre.categorie) ? 'Date facture transmise' : sinistre.categorie === 'sans_expertise' ? 'Facture en attente (depuis)' : "Date rapport d'expertise"}</label>
            <input type="date" value={form.date_rapport_expertise} onChange={e => onChange({ ...form, date_rapport_expertise: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Date clôture</label>
            <input type="date" value={form.date_cloture} onChange={e => onChange({ ...form, date_cloture: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Montant final (€)</label>
            <input type="number" step="0.01" value={form.montant_final} onChange={e => onChange({ ...form, montant_final: e.target.value })} placeholder="Ex : 2 500" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Responsabilité</label>
            <select value={form.responsabilite} onChange={e => onChange({ ...form, responsabilite: e.target.value })} style={inputStyle}>
              <option value="">— Non déterminée —</option>
              {RESPONSABILITE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Commentaire de clôture</label>
            <textarea value={form.commentaire_cloture} onChange={e => onChange({ ...form, commentaire_cloture: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Résolution, observations…" />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={onSubmit}
          style={{ flex: 1, background: '#1a2b4a', color: 'white', padding: '11px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Enregistrer
        </button>
        <button onClick={onCancel}
          style={{ padding: '11px 18px', background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
          Annuler
        </button>
      </div>
    </>
  );
}

function SinistreDetailModal({ sinistre, onEdit, onAdvance, onClose }) {
  const sc = STATUT_SIN_COLORS[sinistre.statut] || STATUT_SIN_COLORS['Ouvert'];
  const cc = CATEGORIE_COLORS[sinistre.categorie] || { bg: '#f3f4f6', text: '#374151' };
  const canAdvance = getNextStep(sinistre) !== null;
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/sinistres/${sinistre.id}/documents`)
      .then(r => setDocs(r.data))
      .catch(() => {});
  }, [sinistre.id]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/sinistres/${sinistre.id}/documents`, fd);
      setDocs(prev => [...prev, res.data]);
    } catch {
      alert('Erreur lors de l\'upload');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const supprimerDoc = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.nom_fichier}" ?`)) return;
    await axios.delete(`${API}/sinistres/${sinistre.id}/documents/${doc.id}`);
    setDocs(prev => prev.filter(d => d.id !== doc.id));
  };

  const getFileIcon = (nom) => {
    const ext = nom.split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return '📄';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
    if (['doc', 'docx'].includes(ext)) return '📝';
    return '📎';
  };

  const row = (label, value) => (
    <div style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', padding: '8px 0' }}>
      <div style={{ width: '170px', flexShrink: 0, fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{label}</div>
      <div style={{ fontSize: '13px', color: '#1a2b4a', fontWeight: '500' }}>{value || '—'}</div>
    </div>
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <h2 style={{ color: '#1a2b4a', fontSize: '17px', fontWeight: '700', margin: '0 0 4px', fontFamily: 'monospace' }}>{sinistre.numero}</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: '2px 9px', borderRadius: '99px', fontSize: '11px', fontWeight: '600' }}>{sinistre.statut}</span>
            <span style={{ background: cc.bg, color: cc.text, padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: '600' }}>{CATEGORIE_LABELS[sinistre.categorie] || '—'}</span>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '2px' }}>×</button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        {row('N° contrat assurance', sinistre.num_contrat_assurance)}
        {row('Plaque immatriculation', sinistre.plaque_immatriculation)}
        {row('Type de véhicule', sinistre.type_vehicule)}
        {row('Date sinistre', fmtDate(sinistre.date_sinistre))}
        {RECOURS_CATS.includes(sinistre.categorie) ? (
          <>
            {row('Date facture transmise', fmtDate(sinistre.date_rapport_expertise))}
            {row('Date règlement', fmtDate(sinistre.date_reglement))}
            {sinistre.montant_reglement != null && row('Montant réglé', fmtEur(sinistre.montant_reglement))}
            {/* Le n° de sinistre a aussi sa place sur un dossier de recours :
                il était jusqu'ici masqué par le montant qui l'écrasait. */}
            {row('N° sinistre assurance', sinistre.num_sinistre_assurance)}
          </>
        ) : (
          <>
            {row('N° sinistre assurance', sinistre.num_sinistre_assurance)}
            {row('Date ordre de mission', fmtDate(sinistre.date_ordre_mission))}
            {row(sinistre.categorie === 'sans_expertise' ? 'Facture en attente (depuis)' : 'Date rapport expertise', fmtDate(sinistre.date_rapport_expertise))}
          </>
        )}
        {row('Date clôture', fmtDate(sinistre.date_cloture))}
        {sinistre.montant_final != null && row('Montant final', fmtEur(sinistre.montant_final))}
        {sinistre.responsabilite && (
          <div style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', padding: '8px 0', alignItems: 'center' }}>
            <div style={{ width: '170px', flexShrink: 0, fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Responsabilité</div>
            <span style={{ background: (RESPONSABILITE_COLORS[sinistre.responsabilite] || {}).bg || '#f3f4f6', color: (RESPONSABILITE_COLORS[sinistre.responsabilite] || {}).text || '#6b7280', border: `1px solid ${(RESPONSABILITE_COLORS[sinistre.responsabilite] || {}).border || '#d1d5db'}`, padding: '2px 9px', borderRadius: '99px', fontSize: '11px', fontWeight: '600' }}>
              {sinistre.responsabilite}
            </span>
          </div>
        )}
        {sinistre.commentaire_cloture && row('Commentaire clôture', sinistre.commentaire_cloture)}
      </div>

      <div style={{ background: '#f8fafc', borderRadius: '6px', padding: '10px 12px', marginBottom: '16px', fontSize: '12px', color: '#6b7280' }}>
        {getStepLabel(sinistre)}
      </div>

      {/* Pièces jointes */}
      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a2b4a' }}>Pièces jointes
            {docs.length > 0 && <span style={{ color: '#9ca3af', fontWeight: '400', marginLeft: '6px' }}>({docs.length})</span>}
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{ background: '#f0f7ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '5px 12px', borderRadius: '6px', cursor: uploading ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '600', opacity: uploading ? 0.6 : 1 }}>
            {uploading ? 'Upload...' : '+ Ajouter un fichier'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>

        {docs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px', color: '#d1d5db', fontSize: '13px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #e5e7eb' }}>
            Aucun document joint
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {docs.map(doc => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px' }}>
                <span style={{ fontSize: '16px', flexShrink: 0 }}>{getFileIcon(doc.nom_fichier)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: '#1a2b4a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.nom_fichier}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>{new Date(doc.date_upload).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                <a href={urlFichier(`${API}/uploads/sinistres/${doc.chemin}`)} target="_blank" rel="noopener noreferrer"
                  style={{ background: '#1a2b4a', color: 'white', border: 'none', borderRadius: '5px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', textDecoration: 'none', flexShrink: 0 }}>
                  ⬇ DL
                </a>
                <button title="Supprimer" onClick={() => supprimerDoc(doc)}
                  style={{ background: 'none', border: '1px solid #fecaca', color: '#ef4444', borderRadius: '5px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}><IconeCorbeille taille={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        {canAdvance && (
          <button onClick={() => { onClose(); onAdvance(); }} style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
            Avancer le workflow →
          </button>
        )}
        <button onClick={() => { onClose(); onEdit(); }} style={{ background: 'white', color: '#374151', border: '1px solid #e5e7eb', padding: '9px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>Modifier
        </button>
        <button onClick={onClose} style={{ marginLeft: 'auto', background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', padding: '9px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
          Fermer
        </button>
      </div>
    </>
  );
}

function AdvanceSinistreModal({ sinistre, advanceData, onChange, onSubmit, onCancel }) {
  const nextStep = getNextStep(sinistre);
  const titles = {
    num_sinistre:      'Saisir le n° sinistre assurance',
    rapport:           "Date du rapport d'expertise",
    cloture:           'Clôturer le dossier',
    facture:           'En attente facture client',
    facture_transmise: 'Facture transmise',
    reglement_facture: 'Règlement facture',
  };
  const isValid = () => {
    if (nextStep === 'num_sinistre') return !!advanceData.num_sinistre_assurance;
    if (nextStep === 'rapport')      return !!advanceData.date_rapport_expertise;
    return true;
  };

  return (
    <>
      <h2 style={{ ...titreSection, marginBottom: E.xs }}>{titles[nextStep]}</h2>
      <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 20px' }}>
        {sinistre.numero} — {sinistre.plaque_immatriculation || 'sans plaque'} — {CATEGORIE_LABELS[sinistre.categorie]}
      </p>

      {nextStep === 'num_sinistre' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
          <div>
            <label style={labelStyle}>N° sinistre assurance *</label>
            <input autoFocus value={advanceData.num_sinistre_assurance || ''} onChange={e => onChange({ ...advanceData, num_sinistre_assurance: e.target.value })} placeholder="Ex: HEL-2024-XXXXX" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Date ordre de mission (auto : aujourd'hui)</label>
            <input type="date" value={advanceData.date_ordre_mission || todayStr()} onChange={e => onChange({ ...advanceData, date_ordre_mission: e.target.value })} style={inputStyle} />
          </div>
        </div>
      )}

      {nextStep === 'rapport' && (
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Date du rapport d'expertise *</label>
          <input autoFocus type="date" value={advanceData.date_rapport_expertise || ''} onChange={e => onChange({ ...advanceData, date_rapport_expertise: e.target.value })} style={inputStyle} />
        </div>
      )}

      {nextStep === 'facture' && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '14px 16px', marginBottom: '20px', fontSize: '13px', color: '#0369a1', lineHeight: 1.6 }}>Cliquez sur <strong>Valider</strong> pour marquer le dossier comme "En attente facture client".<br />
          Vous pourrez ensuite le clôturer une fois la facture reçue.
        </div>
      )}

      {nextStep === 'facture_transmise' && (
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Date de transmission de la facture</label>
          <input autoFocus type="date" value={advanceData.date_facture_transmise || todayStr()} onChange={e => onChange({ ...advanceData, date_facture_transmise: e.target.value })} style={inputStyle} />
        </div>
      )}

      {nextStep === 'reglement_facture' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
          <div>
            <label style={labelStyle}>Date de règlement</label>
            <input autoFocus type="date" value={advanceData.date_reglement || todayStr()} onChange={e => onChange({ ...advanceData, date_reglement: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Montant réglé (€)</label>
            <input type="number" step="0.01" value={advanceData.montant_reglement || ''} onChange={e => onChange({ ...advanceData, montant_reglement: e.target.value })} placeholder="Ex : 1 500" style={inputStyle} />
          </div>
        </div>
      )}

      {nextStep === 'cloture' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
          <div>
            <label style={labelStyle}>Date de clôture</label>
            <input type="date" value={advanceData.date_cloture || todayStr()} onChange={e => onChange({ ...advanceData, date_cloture: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Montant final (€)</label>
              <input type="number" step="0.01" value={advanceData.montant_final || ''} onChange={e => onChange({ ...advanceData, montant_final: e.target.value })} placeholder="Ex : 2 500" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Responsabilité</label>
              <select value={advanceData.responsabilite || ''} onChange={e => onChange({ ...advanceData, responsabilite: e.target.value })} style={inputStyle}>
                <option value="">— Non déterminée —</option>
                {RESPONSABILITE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Commentaire de clôture</label>
            <textarea value={advanceData.commentaire_cloture || ''} onChange={e => onChange({ ...advanceData, commentaire_cloture: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Résolution, observations…" />
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={onSubmit} disabled={!isValid()}
          style={{ flex: 1, background: nextStep === 'cloture' ? '#059669' : '#1a2b4a', color: 'white', padding: '11px', border: 'none', borderRadius: '8px', cursor: isValid() ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '500', opacity: isValid() ? 1 : 0.5 }}>
          {nextStep === 'cloture' ? '✅ Clôturer le dossier' : 'Valider →'}
        </button>
        <button onClick={onCancel} style={{ padding: '11px 18px', background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Annuler</button>
      </div>
    </>
  );
}

const parserCopie = (texte) => {
  if (!texte) return [];
  return texte
    .split(/[,;\s]+/)
    .map(s => s.trim())
    .filter(Boolean);
};

function AppelPrimeModal({ contrat, onClose }) {
  const [appels, setAppels]               = useState([]);
  const [showForm, setShowForm]           = useState(false);
  const [form, setForm]                   = useState({ montant: '', date_envoi: todayStr() });
  const [reglementId, setReglementId]     = useState(null);
  const [reglementDate, setReglementDate] = useState(todayStr());
  const [docsOpen, setDocsOpen]           = useState(null);
  const [docs, setDocs]                   = useState({});
  const [uploadingFor, setUploadingFor]   = useState(null);
  const fileRef          = useRef(null);
  const pendingUploadRef = useRef(null);

  // Appel de prime généré par le CRM et envoyé par mail. Les prérequis disent,
  // avant d'ouvrir le formulaire, si le contrat est prêt (numéro, compagnie
  // dont le RIB est configuré) : mieux vaut un motif lisible qu'un formulaire
  // rempli puis refusé.
  const [prerequis, setPrerequis]   = useState(null);
  const [showGen, setShowGen]       = useState(false);
  const [gen, setGen]               = useState({ numero_assureur: '', montant: '', periode_debut: '', periode_fin: '', destinataire: '', copie: '' });
  const [genEnvoi, setGenEnvoi]     = useState(false);
  const [genMessage, setGenMessage] = useState(null); // { ok: bool, texte: string }

  useEffect(() => {
    axios.get(`${API}/contrats-assurance/${contrat.id}/appels-prime/prerequis`)
      .then(r => { setPrerequis(r.data); setGen(g => ({ ...g, destinataire: g.destinataire || r.data.destinataire || '' })); })
      .catch(() => setPrerequis({ pret: false, motif: 'Impossible de vérifier le contrat.' }));
  }, [contrat.id]);

  const generer = async () => {
    setGenEnvoi(true);
    setGenMessage(null);
    try {
      const res = await axios.post(`${API}/contrats-assurance/${contrat.id}/appels-prime/generer`, {
        numero_assureur: gen.numero_assureur.trim(),
        montant: parseFloat(gen.montant),
        periode_debut: gen.periode_debut,
        periode_fin: gen.periode_fin,
        destinataire: gen.destinataire.trim(),
        copie: parserCopie(gen.copie),
      });
      loadAppels();
      if (res.data.mail_envoye) {
        setGenMessage({ ok: true, texte: `✅ Appel de prime envoyé à ${gen.destinataire.trim()}. Il est aussi enregistré ici, et visible par le client dans son espace.` });
        setShowGen(false);
        setGen(g => ({ ...g, numero_assureur: '', montant: '', periode_debut: '', periode_fin: '', copie: '' }));
      } else {
        // Le document existe, le mail n'est pas parti : on le dit, et « Renvoyer »
        // le reprend. Le taire ferait croire le client prévenu.
        setGenMessage({ ok: false, texte: `⚠️ Le document est enregistré mais le mail n'est PAS parti : ${res.data.erreur || 'erreur inconnue'}. Utilisez « Renvoyer » sur la ligne ci-dessous.` });
        setShowGen(false);
      }
    } catch (err) {
      setGenMessage({ ok: false, texte: `❌ ${err.response?.data?.detail || 'La génération a échoué.'}` });
    }
    setGenEnvoi(false);
  };

  const renvoyer = async (appel) => {
    const adresse = window.prompt("Adresse email du destinataire :", appel.envoye_a || (prerequis && prerequis.destinataire) || '');
    if (!adresse) return;
    const copieSaisie = window.prompt("Adresses en copie (facultatif) :", appel.copie_a || '');
    if (copieSaisie === null) return;
    try {
      await axios.post(`${API}/appels-prime/${appel.id}/envoyer`, {
        destinataire: adresse.trim(),
        copie: parserCopie(copieSaisie),
      });
      setGenMessage({ ok: true, texte: `✅ Appel de prime renvoyé à ${adresse.trim()}.` });
      loadAppels();
    } catch (err) {
      setGenMessage({ ok: false, texte: `❌ ${err.response?.data?.detail || "L'envoi a échoué."}` });
    }
  };

  const loadAppels = () =>
    axios.get(`${API}/contrats-assurance/${contrat.id}/appels-prime`)
      .then(r => setAppels(r.data)).catch(() => {});

  useEffect(() => { loadAppels(); }, [contrat.id]);

  const loadDocs = (appelId) =>
    axios.get(`${API}/appels-prime/${appelId}/documents`)
      .then(r => setDocs(prev => ({ ...prev, [appelId]: r.data }))).catch(() => {});

  const toggleDocs = (appelId) => {
    if (docsOpen === appelId) { setDocsOpen(null); return; }
    setDocsOpen(appelId);
    if (!docs[appelId]) loadDocs(appelId);
  };

  const submitCreate = async () => {
    try {
      await axios.post(`${API}/contrats-assurance/${contrat.id}/appels-prime`, {
        montant: parseFloat(form.montant) || null,
        date_envoi: form.date_envoi || null,
      });
      loadAppels();
      setShowForm(false);
      setForm({ montant: '', date_envoi: todayStr() });
    } catch { alert('Erreur lors de la création.'); }
  };

  const submitReglement = async (appelId) => {
    try {
      await axios.patch(`${API}/contrats-assurance/${contrat.id}/appels-prime/${appelId}`, {
        date_reglement: reglementDate || todayStr(),
        statut: 'regle',
      });
      loadAppels();
      setReglementId(null);
    } catch { alert('Erreur.'); }
  };

  const deleteAppel = async (appelId) => {
    if (!window.confirm('Supprimer cet appel de prime ?')) return;
    await axios.delete(`${API}/contrats-assurance/${contrat.id}/appels-prime/${appelId}`);
    loadAppels();
  };

  const triggerUpload = (appelId) => {
    pendingUploadRef.current = appelId;
    fileRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    const appelId = pendingUploadRef.current;
    if (!file || !appelId) return;
    setUploadingFor(appelId);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/appels-prime/${appelId}/documents`, fd);
      setDocs(prev => ({ ...prev, [appelId]: [...(prev[appelId] || []), res.data] }));
    } catch { alert('Erreur upload.'); }
    setUploadingFor(null);
    pendingUploadRef.current = null;
    if (fileRef.current) fileRef.current.value = '';
  };

  const deleteDoc = async (appelId, docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    await axios.delete(`${API}/appels-prime/${appelId}/documents/${docId}`);
    setDocs(prev => ({ ...prev, [appelId]: (prev[appelId] || []).filter(d => d.id !== docId) }));
  };

  const totalAttendu = appels.reduce((s, a) => s + (a.montant || 0), 0);
  const totalRegle   = appels.filter(a => a.statut === 'regle').reduce((s, a) => s + (a.montant || 0), 0);
  const restant      = totalAttendu - totalRegle;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <h2 style={{ ...titreSection, marginBottom: E.xs }}>Appels de prime</h2>
          <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>
            {contrat.numero}{contrat.num_contrat ? ` — ${contrat.num_contrat}` : ''}
            {contrat.compagnie ? <span style={{ color: '#9ca3af' }}> · {contrat.compagnie}</span> : ''}
          </p>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
      </div>

      {appels.length > 0 && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          {[
            { label: 'Total attendu', value: fmtEur(totalAttendu), bg: '#f8fafc', border: '#e5e7eb', color: '#1a2b4a' },
            { label: 'Réglé',         value: fmtEur(totalRegle),   bg: '#f0fdf4', border: '#bbf7d0', color: '#059669' },
            { label: 'Restant',       value: fmtEur(restant),      bg: restant > 0 ? '#fff7ed' : '#f8fafc', border: restant > 0 ? '#fed7aa' : '#e5e7eb', color: restant > 0 ? '#c2410c' : '#6b7280' },
          ].map(({ label, value, bg, border, color }) => (
            <div key={label} style={{ flex: 1, background: bg, borderRadius: '8px', padding: '10px 12px', border: `1px solid ${border}` }}>
              <div style={{ fontSize: '10px', color, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{label}</div>
              <div style={{ fontSize: '15px', fontWeight: '700', color }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
        <button onClick={() => { setShowGen(!showGen); setShowForm(false); setGenMessage(null); }}
          disabled={!prerequis || !prerequis.pret}
          title={prerequis && !prerequis.pret ? prerequis.motif : 'Générer le document et l\'envoyer au client par mail'}
          style={{ background: prerequis && prerequis.pret ? '#2f6fa8' : '#d1d5db', color: 'white', padding: '7px 14px', border: 'none', borderRadius: '6px', cursor: prerequis && prerequis.pret ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: '600' }}>
          📄 Appel de prime à envoyer
        </button>
        {/* Suivi interne seulement : aucun document, aucun mail. Le libellé le dit,
            pour qu'on ne le confonde pas avec « Appel de prime à envoyer ». */}
        <button onClick={() => { setShowForm(!showForm); setShowGen(false); }}
          title="Enregistre un appel de prime pour le suivi : aucun document n'est généré et rien n'est envoyé au client"
          style={{ background: '#1a2b4a', color: 'white', padding: '7px 14px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
          + Noter un appel (sans envoi)
        </button>
      </div>

      {prerequis && !prerequis.pret && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px' }}>
          ℹ️ {prerequis.motif}
        </div>
      )}

      {genMessage && (
        <div style={{ background: genMessage.ok ? '#f0fdf4' : '#fef2f2', border: `1px solid ${genMessage.ok ? '#bbf7d0' : '#fecaca'}`, color: genMessage.ok ? '#166534' : '#991b1b', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', fontWeight: '500', marginBottom: '12px' }}>
          {genMessage.texte}
        </div>
      )}

      {showGen && (
        <div style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a2b4a', marginBottom: '4px' }}>Appel de prime à envoyer</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
            Contrat n° {prerequis?.num_contrat} · {prerequis?.compagnie}. Le contrat, la compagnie (et son RIB), le client, votre nom et la date se reprennent seuls.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>N° d'appel de prime (celui de l'assureur) *</label>
              <input value={gen.numero_assureur} onChange={e => setGen({ ...gen, numero_assureur: e.target.value })} placeholder="Ex : 202624212907036" style={inputStyle} autoFocus />
            </div>
            <div>
              <label style={labelStyle}>Montant (€) *</label>
              <input type="number" step="0.01" min="0" value={gen.montant} onChange={e => setGen({ ...gen, montant: e.target.value })} placeholder="Ex : 33818.39" style={inputStyle} />
            </div>
            <div />
            <div>
              <label style={labelStyle}>Période : du *</label>
              <input type="date" value={gen.periode_debut} onChange={e => setGen({ ...gen, periode_debut: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>au *</label>
              <input type="date" value={gen.periode_fin} onChange={e => setGen({ ...gen, periode_fin: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Envoyer à (email du client) *</label>
              <input type="email" value={gen.destinataire} onChange={e => setGen({ ...gen, destinataire: e.target.value })} placeholder="client@entreprise.fr" style={inputStyle} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Copie (cc)</label>
              <input type="text" value={gen.copie} onChange={e => setGen({ ...gen, copie: e.target.value })} placeholder="adresse1@exemple.fr, adresse2@exemple.fr" style={inputStyle} />
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Facultatif — plusieurs adresses séparées par une virgule.</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={generer}
              disabled={genEnvoi || !gen.numero_assureur.trim() || !gen.montant || !gen.periode_debut || !gen.periode_fin || !gen.destinataire.trim()}
              style={{ background: genEnvoi ? '#9ca3af' : '#2f6fa8', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: genEnvoi ? 'wait' : 'pointer', fontSize: '13px', fontWeight: '600', opacity: (!gen.numero_assureur.trim() || !gen.montant || !gen.periode_debut || !gen.periode_fin || !gen.destinataire.trim()) ? 0.5 : 1 }}>
              {genEnvoi ? 'Envoi…' : '📄 Générer et envoyer'}
            </button>
            <button onClick={() => setShowGen(false)} style={{ background: 'white', color: '#6b7280', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Annuler</button>
          </div>
        </div>
      )}

      {showForm && (
        <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={labelStyle}>Montant (€)</label>
              <input type="number" step="0.01" value={form.montant} onChange={e => setForm({ ...form, montant: e.target.value })} placeholder="Ex: 1 200" style={inputStyle} autoFocus />
            </div>
            <div>
              <label style={labelStyle}>Date d'envoi</label>
              <input type="date" value={form.date_envoi} onChange={e => setForm({ ...form, date_envoi: e.target.value })} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={submitCreate} style={{ background: '#1a2b4a', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>Créer</button>
            <button onClick={() => setShowForm(false)} style={{ background: 'white', color: '#6b7280', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Annuler</button>
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx" style={{ display: 'none' }} onChange={handleFileUpload} />

      {appels.length === 0 && !showForm ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: '14px' }}>Aucun appel de prime. Utilisez « Appel de prime à envoyer » pour en adresser un au client, ou « Noter un appel » pour le suivi seul.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {appels.map(a => {
            const isRegle    = a.statut === 'regle';
            const isReglant  = reglementId === a.id;
            const appelDocs  = docs[a.id] || [];
            const isDocsOpen = docsOpen === a.id;
            return (
              <div key={a.id} style={{ border: `1px solid ${isRegle ? '#bbf7d0' : '#e5e7eb'}`, borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: isRegle ? '#f0fdf4' : 'white' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: '700', color: '#1a2b4a', minWidth: '56px' }}>{a.numero}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: isRegle ? '#059669' : '#1a2b4a' }}>{fmtEur(a.montant)}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                      {a.numero_assureur ? `n° ${a.numero_assureur} · ` : ''}
                      {a.periode_debut ? `du ${fmtDate(a.periode_debut)} au ${fmtDate(a.periode_fin)} · ` : ''}
                      Envoi : {fmtDate(a.date_envoi)}{isRegle && a.date_reglement ? ` · Réglé : ${fmtDate(a.date_reglement)}` : ''}
                    </div>
                    {/* Un appel généré dit s'il est réellement parti : « créé » et
                        « envoyé » ne sont pas la même chose. */}
                    {a.numero_assureur && (
                      <div style={{ fontSize: '11px', marginTop: '2px', fontWeight: '600', color: a.envoye_a ? '#15803d' : '#b45309' }}>
                        {a.envoye_a ? `✉️ Envoyé à ${a.envoye_a}${a.copie_a ? ` — copie : ${a.copie_a}` : ''}` : '⚠️ Mail non envoyé'}
                      </div>
                    )}
                  </div>
                  <span style={{ background: isRegle ? '#d1fae5' : '#fef3c7', color: isRegle ? '#065f46' : '#92400e', border: `1px solid ${isRegle ? '#6ee7b7' : '#fde68a'}`, padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    {isRegle ? '✅ Réglé' : '⏳ En attente'}
                  </span>
                  {a.numero_assureur && (
                    <button onClick={() => renvoyer(a)} title="Renvoyer le document par mail"
                      style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>Renvoyer
                    </button>
                  )}
                  {!isRegle && (
                    <button onClick={() => { setReglementId(a.id); setReglementDate(todayStr()); }}
                      style={{ background: '#f0fdf4', color: '#059669', border: '1px solid #bbf7d0', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>Régler
                    </button>
                  )}
                  <button onClick={() => toggleDocs(a.id)}
                    style={{ background: isDocsOpen ? '#eff6ff' : 'none', color: '#1d4ed8', border: `1px solid ${isDocsOpen ? '#bfdbfe' : '#e5e7eb'}`, padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>{appelDocs.length > 0 ? ` (${appelDocs.length})` : ''}
                  </button>
                  <button title="Supprimer" onClick={() => deleteAppel(a.id)}
                    style={{ background: 'none', border: '1px solid #fecaca', color: '#ef4444', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}><IconeCorbeille taille={15} />
                  </button>
                </div>

                {isReglant && (
                  <div style={{ padding: '10px 14px', background: '#f0fdf4', borderTop: '1px solid #bbf7d0' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Date de règlement</label>
                        <input type="date" value={reglementDate} onChange={e => setReglementDate(e.target.value)} style={inputStyle} autoFocus />
                      </div>
                      <button onClick={() => submitReglement(a.id)}
                        style={{ background: '#059669', color: 'white', padding: '9px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}>Confirmer
                      </button>
                      <button onClick={() => setReglementId(null)}
                        style={{ background: 'white', color: '#6b7280', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {isDocsOpen && (
                  <div style={{ padding: '10px 14px', background: '#f8fafc', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Pièces jointes</div>
                      <button onClick={() => triggerUpload(a.id)} disabled={uploadingFor === a.id}
                        style={{ background: '#f0f7ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', opacity: uploadingFor === a.id ? 0.6 : 1 }}>
                        {uploadingFor === a.id ? 'Upload...' : '+ Fichier'}
                      </button>
                    </div>
                    {appelDocs.length === 0 ? (
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>Aucun document.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {appelDocs.map(doc => (
                          <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 10px' }}>
                            <span style={{ fontSize: '14px' }}>{doc.nom_fichier.toLowerCase().endsWith('.pdf') ? '📄' : '📎'}</span>
                            <div style={{ flex: 1, fontSize: '12px', fontWeight: '500', color: '#1a2b4a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.nom_fichier}</div>
                            <a href={urlFichier(`${API}/uploads/appels-prime/${doc.chemin}`)} target="_blank" rel="noopener noreferrer"
                              style={{ background: '#1a2b4a', color: 'white', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: '500', textDecoration: 'none', flexShrink: 0 }}>⬇ DL</a>
                            <button title="Supprimer" onClick={() => deleteDoc(a.id, doc.id)}
                              style={{ background: 'none', border: '1px solid #fecaca', color: '#ef4444', borderRadius: '4px', padding: '3px 6px', cursor: 'pointer', fontSize: '11px', flexShrink: 0 }}><IconeCorbeille taille={15} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', padding: '9px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
          Fermer
        </button>
      </div>
    </>
  );
}
